import { useState, type ReactNode } from "react";
import { ScrollView, StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import type { CurrentMax, Draft, DraftLineOptions, UnitsPreference } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { DayMenu, type DayMenuMode } from "./DayMenu";
import { DayStructure } from "./DayStructure";
import { ExerciseRoundGrid } from "./ExerciseRoundGrid";
import type { useRevealFocusedField } from "./useRevealFocusedField";
import type { V3DayActions } from "./useV3DayActions";
import type { V3Navigation } from "./useV3Navigation";
import { SCROLL_END, type V3Styles } from "./v3Styles";

/** The mock's zones (`.wg`): the structure and the exercise, in pt. */
const LEFT_W = 240;
const RIGHT_W = 330;

// The editor's body on a wide screen (#336 slice 6, brief §7.3). From 1100
// three zones as the mock draws them: the structure, the day's grid, the open
// exercise. The grid carries the heatmap's bands (§15 row 28). Between 768
// and 1100 the structure goes and the day chips with «+ Day» take its place,
// the day menu moves into the grid's head. The exercise pane is the same slot in both, so a resize keeps
// it (useMasterDetailSlots' reason). `top` and `panels` are what the phone
// scroller has above and below the day.
export function WideEditorBody({
  theme,
  styles,
  tier,
  draft,
  nav,
  days,
  lineOptions,
  cellUnit,
  resolveName,
  maxMeta,
  onAddExercise,
  onSetCount,
  top,
  navigator,
  panels,
  detail,
  detailScroll,
}: {
  theme: UiTheme;
  styles: V3Styles;
  tier: "medium" | "large";
  draft: Draft;
  nav: V3Navigation;
  days: V3DayActions;
  lineOptions: DraftLineOptions;
  cellUnit: UnitsPreference;
  resolveName: (id: string, snapshot?: string) => string;
  maxMeta?: ReadonlyMap<string, CurrentMax>;
  onAddExercise: () => void;
  /** The grid's «Sets» lens: one round's set count. */
  onSetCount: (slotIdx: number, w: number, n: number) => void;
  top: ReactNode;
  /** The day chips, where there is no structure. */
  navigator: ReactNode;
  panels: ReactNode;
  /** The open exercise, or null when the day has none. */
  detail: ReactNode;
  /** The keyboard reveal, on the pane where the fields are. */
  detailScroll: Pick<ReturnType<typeof useRevealFocusedField>, "scrollRef" | "onScroll" | "keyboardHeight">;
}) {
  const { t } = useTranslation();
  const p = theme.palette;
  const [menu, setMenu] = useState<DayMenuMode>(null);
  const openDayMenu = (sIdx: number) => {
    if (sIdx !== nav.dayIdx) nav.selectDay(sIdx);
    setMenu("menu");
  };
  return (
    <View style={local.columns}>
      {tier === "large" ? (
        <ScrollView style={[local.left, { borderRightColor: p.border, backgroundColor: p.surface }]}>
          <DayStructure
            theme={theme}
            styles={styles}
            draft={draft}
            nav={nav}
            days={days}
            resolveName={resolveName}
            onDayMenu={openDayMenu}
          />
        </ScrollView>
      ) : null}
      <ScrollView style={local.center} contentContainerStyle={local.scrollEnd} keyboardShouldPersistTaps="handled">
        {top}
        {navigator}
        <View style={styles.gutter}>
          <DayMenu theme={theme} styles={styles} draft={draft} sIdx={nav.dayIdx} mode={menu} setMode={setMenu} days={days} />
        </View>
        <ExerciseRoundGrid
          theme={theme}
          styles={styles}
          draft={draft}
          nav={nav}
          days={days}
          lineOptions={lineOptions}
          cellUnit={cellUnit}
          resolveName={resolveName}
          maxMeta={maxMeta}
          onAddExercise={onAddExercise}
          onSetCount={onSetCount}
          onDayMenu={tier === "medium" ? () => openDayMenu(nav.dayIdx) : undefined}
        />
        {panels}
      </ScrollView>
      <ScrollView
        ref={detailScroll.scrollRef}
        style={[local.right, { borderLeftColor: p.border }]}
        contentContainerStyle={{ paddingBottom: SCROLL_END + detailScroll.keyboardHeight }}
        onScroll={detailScroll.onScroll}
        scrollEventThrottle={32}
        keyboardShouldPersistTaps="handled"
      >
        {detail ?? (
          <Text style={[styles.section, styles.muted]}>
            {t("editorV3.grid.pickExercise", { defaultValue: "Pick an exercise in the grid to see it here." })}
          </Text>
        )}
      </ScrollView>
    </View>
  );
}

const local = StyleSheet.create({
  columns: { flex: 1, flexDirection: "row" },
  left: { width: LEFT_W, flexGrow: 0, borderRightWidth: 1 },
  center: { flex: 1 },
  right: { width: RIGHT_W, flexGrow: 0, borderLeftWidth: 1 },
  scrollEnd: { paddingBottom: SCROLL_END },
});
